import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "./ui/dialog";
import { Star, MessageSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import AutoScrollRow, { RowArrows } from "./AutoScrollRow";

function Stars({ value, onChange, size = 4 }) {
  const readonly = !onChange;
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          type="button"
          key={n}
          onClick={() => onChange && onChange(n)}
          disabled={readonly}
          className={readonly ? "cursor-default" : "cursor-pointer"}
          aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
          data-testid={onChange ? `review-star-${n}` : undefined}
        >
          <Star className={`w-${size} h-${size} ${n <= value ? "fill-orange-400 text-orange-400" : "text-zinc-700"}`} />
        </button>
      ))}
    </div>
  );
}

// « Membre depuis mars 2025 » : mois et année d'inscription de l'auteur (fournis par le serveur, « 2025-03 »)
const memberSince = (ym) => {
  if (!ym) return null;
  const [y, m] = ym.split("-").map(Number);
  if (!y || !m) return null;
  return new Date(y, m - 1, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
};
function ReviewBody({ r, clamp }) {
  const since = memberSince(r.member_since);
  return (
    <div className="flex flex-col h-full">
      <Stars value={r.rating} />
      <p className={`mt-3 text-sm text-zinc-300 leading-relaxed ${clamp ? "line-clamp-6" : ""}`}>« {r.comment} »</p>
      <div className="mt-auto pt-4 text-xs text-zinc-500 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="text-zinc-300 truncate">{r.pseudonym}</div>
          {since && <div className="mt-0.5 text-[11px] text-zinc-500" data-testid="review-member-since">Membre depuis {since}</div>}
        </div>
        <span className="font-mono-num shrink-0">{r.created_at ? new Date(r.created_at).toLocaleDateString("fr-FR") : ""}</span>
      </div>
    </div>
  );
}
export default function ReviewsSection() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: reviews } = useQuery({ queryKey: ["reviews"], queryFn: async () => (await api.get("/reviews")).data });
  const [open, setOpen] = useState(false);
  const rowRef = useRef(null);
  const [pseudonym, setPseudonym] = useState("");
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  const submit = useMutation({
    mutationFn: async () => (await api.post("/reviews", { pseudonym, rating, comment })).data,
    onSuccess: () => {
      setOpen(false); setComment(""); setPseudonym(""); setRating(5);
      qc.invalidateQueries({ queryKey: ["reviews"] });
      toast.success("Merci ! Votre avis sera publié après validation.");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Erreur"),
  });

  return (
    <section className="mx-auto max-w-7xl px-6 pt-16 pb-8 border-t border-white/5" data-testid="reviews-section">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-400 mb-2">Communauté</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl">Ce qu'ils en disent</h2>
        </div>
        <div className="flex flex-wrap items-center gap-4">
        {reviews?.length >= 4 && <RowArrows onPrev={() => rowRef.current?.prev()} onNext={() => rowRef.current?.next()} testid="reviews" />}
        {user ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-500 text-black hover:bg-emerald-300" data-testid="review-add-btn">
                <MessageSquare className="w-4 h-4 mr-1.5" /> Laisser un avis
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-white/10 max-w-md" data-testid="review-dialog">
              <DialogHeader><DialogTitle>Partager mon expérience</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-zinc-400">Pseudo (public)</label>
                  <Input value={pseudonym} onChange={(e) => setPseudonym(e.target.value)} className="mt-1.5 bg-white/[0.03] border-white/10" data-testid="review-pseudonym" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-zinc-400">Note</label>
                  <div className="mt-2"><Stars value={rating} onChange={setRating} size={6} /></div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-zinc-400">Commentaire</label>
                  <Textarea rows={4} value={comment} onChange={(e) => setComment(e.target.value)} className="mt-1.5 bg-white/[0.03] border-white/10" data-testid="review-comment" />
                </div>
                <p className="text-xs text-zinc-500">Votre email reste privé — seul le pseudo est affiché.</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button onClick={() => submit.mutate()} disabled={submit.isPending || pseudonym.length < 2 || comment.length < 8} className="bg-emerald-500 text-black hover:bg-emerald-300" data-testid="review-submit">
                  {submit.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Envoyer"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ) : (
          <Button asChild variant="outline" className="border-white/10 bg-transparent">
            <Link to="/login">Se connecter pour laisser un avis</Link>
          </Button>
        )}
        </div>
      </div>

      {reviews && reviews.length > 0 ? (
        reviews.length >= 4 ? (
          // Défile tout seul ; souris dessus = pause, puis molette, glisser, doigt ou flèches
          <AutoScrollRow
            ref={rowRef}
            items={reviews}
            getKey={(r) => r.id}
            renderItem={(r, idx) => (
              <Card className="p-5 bg-card border-white/5 w-[340px] h-full" data-testid={idx < reviews.length ? `review-${r.id}` : undefined}>
                <ReviewBody r={r} clamp />
              </Card>
            )}
            speed={26}
            testid="reviews-carousel"
          />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {reviews.map((r) => (
              <Card key={r.id} className="p-5 bg-card border-white/5" data-testid={`review-${r.id}`}>
                <ReviewBody r={r} />
              </Card>
            ))}
          </div>
        )
      ) : (
        <div className="text-center py-12 text-zinc-500 text-sm">Aucun avis pour l'instant. Soyez le premier !</div>
      )}
    </section>
  );
}
